import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// types
import { SendDialogTypes } from '@makemydeal/dr-dash-types';

// components
import { Modal } from '@interstate/components/Modal';
import { Interstitial as LoadingIndicator } from '@interstate/components/Interstitial';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import { Alert } from '@interstate/components/Alert';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import SendToCustomerDialogInterstate from '../sendToCustomerDialog/SendToCustomerDialog.interstate';

// styles
import { AlertContainerInterstate, LoaderWrapper, ShareToCustomerDialogFooter } from './ShareToCustomerDialog.style';
import { ShareToCustomerDialogMode } from './ShareToCustomerDialogMode';
import type { ShareToCustomerDialogProps } from './ShareToCustomerDialogProps';
import { useValidationGuard } from '../hooks/useValidationGuard';

export const ShareToCustomerDialogInterstate = ({ show, onHide, header, dialogPrompt, prefix }: ShareToCustomerDialogProps) => {
    const dispatch = useDispatch();
    const [mode, setMode] = useState(ShareToCustomerDialogMode.SAVING);
    const offerIsBeingSaved = useSelector(offerInfoSelectors.getIsOfferBeingSaved);
    const offerHasBeenSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const shopperURL = useSelector(offerInfoSelectors.getShopperURL);
    const urlsLoading = useSelector(offerInfoSelectors.getUrlsLoading);
    const urlsError = useSelector(offerInfoSelectors.getUrlsError);

    // Set mode when offer save status or URL generation status changes
    React.useEffect(() => {
        if (show) {
            // Priority 1: If offer is being saved, show SAVING mode
            if (offerIsBeingSaved === true) {
                return setMode(ShareToCustomerDialogMode.SAVING);
            }

            // Priority 2: If offer save failed, show ERROR mode
            if (offerIsBeingSaved === false && offerHasBeenSaved === false) {
                setMode(ShareToCustomerDialogMode.ERROR);
                return;
            }

            // Priority 3: If URLs are being generated, show SAVING mode
            if (urlsLoading === true) {
                return setMode(ShareToCustomerDialogMode.SAVING);
            }

            // Priority 4: If URL generation failed but we have fallback URLs, show SUCCESS mode
            if (urlsError && shopperURL) {
                setMode(ShareToCustomerDialogMode.SUCCESS);
                return;
            }

            // Priority 5: If URL generation failed and no URLs, show ERROR mode
            if (urlsError) {
                setMode(ShareToCustomerDialogMode.ERROR);
                return;
            }

            // Priority 6: If shopperURL exists, show SUCCESS mode
            if (shopperURL) {
                setMode(ShareToCustomerDialogMode.SUCCESS);
                return;
            }

            // Default: Wait for URLs (SAVING mode)
            setMode(ShareToCustomerDialogMode.SAVING);
        }
    }, [offerIsBeingSaved, offerHasBeenSaved, urlsLoading, urlsError, shopperURL, show]);

    const guard = useValidationGuard();

    const handleRetryClick = guard.wrap(() => dispatch(offerActionCreators.saveOffer()));

    switch (mode) {
        case ShareToCustomerDialogMode.SAVING:
            return (
                <Modal
                    show={show}
                    size="large"
                    data-testid={`${prefix}-dialog-saving`}
                    onHide={onHide}
                    header={
                        <Typography variant="h3" color="base.color.blue.900">
                            {header}
                        </Typography>
                    }
                    footer={{ footerComponent: <ShareToCustomerDialogFooter /> }}
                >
                    <LoaderWrapper>
                        <LoadingIndicator message="We're updating your changes to this offer." size="small" />
                    </LoaderWrapper>
                </Modal>
            );

        case ShareToCustomerDialogMode.ERROR: {
            const isUrlGenerationError = urlsError && offerHasBeenSaved;
            const errorTitle = isUrlGenerationError ? 'Link generation failed.' : 'Offer update failed.';
            const errorMessage = isUrlGenerationError
                ? 'We ran into a problem generating the link.'
                : "We ran into a problem updating this offer so the information wasn't shared. Please try again.";

            return (
                <Modal
                    show={show}
                    size="large"
                    data-testid={`${prefix}-dialog-error`}
                    header={
                        <Typography variant="h3" color="base.color.blue.900">
                            {header}
                        </Typography>
                    }
                    footer={{
                        footerComponent: (
                            <ShareToCustomerDialogFooter>
                                <Button
                                    buttonStyle="primary"
                                    data-testid={`${prefix}-retry-button`}
                                    onClick={handleRetryClick}
                                    size="small"
                                >
                                    Retry
                                </Button>
                            </ShareToCustomerDialogFooter>
                        )
                    }}
                    onHide={onHide}
                >
                    {/* TODO: [US1141319] Remove AlertContainerInterstate once interstate fixes the modal padding issue: i.e. it give everything inside of it padding by default */}
                    <AlertContainerInterstate data-testid={`${prefix}-alert-container`}>
                        <Alert
                            role="offerupdatefailedalert"
                            type="error"
                            sx={{
                                marginBottom: '16px'
                            }}
                        >
                            <Typography variant="body-md" tag="span">
                                {errorTitle}
                            </Typography>
                        </Alert>
                        <Typography variant="body-md" tag="div">
                            <div>{errorMessage}</div>
                            <br />
                            <div>If the problem persists, please contact support:</div>
                            <div>1-888-895-2994 or create a support ticket.</div>
                        </Typography>
                    </AlertContainerInterstate>
                </Modal>
            );
        }

        case ShareToCustomerDialogMode.SUCCESS:
            return (
                <SendToCustomerDialogInterstate
                    shareUrl={shopperURL}
                    dialogHeader={header}
                    dialogPrompt={dialogPrompt ?? ''}
                    show={show}
                    onHide={onHide}
                    dialogType={SendDialogTypes.Offer}
                />
            );
    }
};
